import { apiFetch } from '../api-client';
import { mockApprovalsApi, isMockMode } from '../mock/approvals-mock';
import type { PaymentRequest } from './payments';

export type ApproverRole = 'KTT' | 'CEO';
export type ApprovalAction = 'approve' | 'reject' | 'forward';
export type ApprovalItemStatus = 'PENDING' | 'DONE' | 'SKIPPED';
export type QueueType = 'urgent' | 'batch';

export interface ApprovalItem {
  id: string;
  paymentRequestId: string;
  approverRole: ApproverRole;
  approverId?: string | null;
  approverName?: string | null;
  action?: ApprovalAction | null;
  note?: string | null;
  queueType: QueueType;
  status: ApprovalItemStatus;
  createdAt: string;
  actedAt?: string | null;
  paymentRequest: PaymentRequest;
}

export interface ApprovalListResponse {
  urgentCount: number;
  batchCount: number;
  tabCounts?: { pending: number; approved: number; rejected: number; all: number };
  items: ApprovalItem[];
  paging: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
  };
}

export interface ApprovalDetail extends ApprovalItem {
  history?: Array<{
    id: string;
    actorId: string;
    actorName: string;
    actorRole: string;
    action: string;
    fromStatus?: string | null;
    toStatus?: string | null;
    note?: string | null;
    createdAt: string;
  }>;
}

export type ApprovalTab = 'pending' | 'approved' | 'rejected' | 'all';

export interface ListQuery {
  queue?: QueueType | 'all';
  status?: ApprovalItemStatus;
  tab?: ApprovalTab;
  search?: string;
  page?: number;
  limit?: number;
}

function qs(query: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const approvalsApi = {
  async list(query: ListQuery = {}): Promise<ApprovalListResponse> {
    if (isMockMode()) return mockApprovalsApi.list(query);
    return apiFetch(`/api/approvals${qs(query as Record<string, unknown>)}`);
  },
  async get(id: string): Promise<ApprovalDetail> {
    if (isMockMode()) return mockApprovalsApi.get(id);
    return apiFetch(`/api/approvals/${id}`);
  },
  async approve(id: string, note?: string): Promise<ApprovalItem> {
    if (isMockMode()) return mockApprovalsApi.approve(id, note);
    return apiFetch(`/api/approvals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },
  async reject(id: string, reason: string): Promise<ApprovalItem> {
    if (isMockMode()) return mockApprovalsApi.reject(id, reason);
    return apiFetch(`/api/approvals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
  async bulkApprove(ids: string[], note?: string): Promise<{ approved: number; failed: string[] }> {
    if (isMockMode()) return mockApprovalsApi.bulkApprove(ids, note);
    return apiFetch(`/api/approvals/bulk-approve`, {
      method: 'POST',
      body: JSON.stringify({ ids, note }),
    });
  },
};
